// 🌐 CLIENTE HTTP DE /api/analyze — B120 (AI_66)
// Sistema: HEPATO TX RONDÔNIA
// ─────────────────────────────────────────────────────────────
// Wrapper único para chamadas a /api/analyze, espelhando o padrão
// arquitetural validado em ragClient.ts (B95):
//   - getIdToken Firebase com cache + refresh automático
//   - retry forçado em 401 (janela rara: token vivo no client, morto no server)
//   - logging consistente
//
// Diferença vs ragClient: usuário deslogado NÃO degrada graciosamente.
// /api/analyze processa dados de paciente e gera laudos médicos —
// chamada sem auth deve falhar explicitamente (throw), nunca silenciar.
//
// Substituirá os 9 fetchs diretos a /api/analyze espalhados por:
//   - TextoLivre.tsx (1)
//   - useIAAnalise.ts (2)
//   - ListaExamesImagem.tsx (3)
//   - ChatDiscussaoCaso.tsx (1)
//   - useAnaliseContextual.ts (1)
//   - TabelaMarcadores.tsx (1)
//
// CFM 2454/2026 Art. 17: proteção contra acessos não autorizados.
// Débito B131 QUITADO (B204, AI_94): getIdTokenSafe + retry 401
// agora vivem em src/lib/authFetch.ts (fonte única, compartilhada
// com ragClient.ts). Aqui fica só a semântica própria: deslogado
// → throw (laudo médico nunca sai sem autenticação).
// ─────────────────────────────────────────────────────────────

import { postAutenticado } from "./authFetch";
import {
  lerModoTesteIA,
  descreverModoTeste,
  type ModoTesteIA,
} from "./modoTesteIA";
import {
  reidentificarObjeto,
  reidentificarTexto,
} from "../shared/utils/reidentificacao";
import { fetchAutenticado } from "./authFetch";
import { criarLeitorNdjson } from "./lerNdjson";

const ENDPOINT_ANALYZE = "/api/analyze";

// ─────────────────────────────────────────────────────────────
// FUNÇÃO PÚBLICA — chamada autenticada genérica a /api/analyze
// ─────────────────────────────────────────────────────────────
// Genérica em T para acomodar os múltiplos formatos de resposta:
//   - geração de laudo: { content: [], verificacao, _proveniencia }
//   - transcrever-conclusao: { texto, tipoLaudista, status }
//   - análise contextual / chat: shapes próprios
// O caller declara o tipo esperado (ou unknown) e faz o parsing.
//
// Comportamento:
//   - usuário deslogado          → throw Error (não degrada graciosamente)
//   - 401 inicial                → retry 1x com forceRefresh
//   - 401 após retry / 403       → throw com detalhe
//   - 408 / 500 / outros         → throw com detalhe
//   - sucesso                    → retorna body parseado
// ─────────────────────────────────────────────────────────────
// 🧪 B359 (AI_156/AI_158) — MODO DE TESTE DE MODELO (migração p/ a geração 5.x).
// Chaves do localStorage e regras: ver src/lib/modoTesteIA.ts (fonte única).
// O servidor aceita só valores de lista fechada e só de usuário médico; fora
// disso ignora e usa o modelo padrão. Nunca lança (storage bloqueado = sem teste).
function lerModeloTeste(): ModoTesteIA | null {
  return lerModoTesteIA();
}

export async function callAnalyze<T = unknown>(
  body: Record<string, unknown>
): Promise<T> {
  const startTime = Date.now();
  const teste = lerModeloTeste();
  if (teste) {
    body = { ...body, _modeloTeste: teste };
    console.log(`🧪 [analyzeClient] modo de teste: ${descreverModoTeste(teste)}`);
  }

  // Fonte única (authFetch): token + retry 401 + throw em !ok.
  // null = deslogado/refresh falhou → aqui vira ERRO explícito.
  const resp = await postAutenticado<T>(ENDPOINT_ANALYZE, body, "analyzeClient");
  if (resp === null) {
    throw new Error(
      "[analyzeClient] Usuário não autenticado — refaça login para continuar"
    );
  }

  const elapsed = Date.now() - startTime;
  console.log(`🌐 [analyzeClient] ${ENDPOINT_ANALYZE} OK em ${elapsed}ms`);

  // B360 (AI_156): marcadores de identificador (§RGCT-…§) voltam ao valor real
  // aqui, no navegador — o número nunca foi enviado à IA.
  return reidentificarObjeto(resp);
}

// ─────────────────────────────────────────────────────────────
// 🌊 B389 (AI_157) — CHAMADA EM FLUXO (análise geral, Etapa 1)
// ─────────────────────────────────────────────────────────────
// Mesma autenticação e mesmo corpo da callAnalyze, mais `stream: true`.
// O servidor responde em NDJSON (ver lerNdjson.ts); `aoTexto` recebe o
// texto ACUMULADO (já reidentificado — B360) a cada pedaço, e a promessa
// resolve com a MESMA resposta final da callAnalyze.
// Rede: se o servidor responder JSON comum (proxy que não transmite, versão
// antiga), cai no caminho de hoje — nunca quebra por causa do fluxo.
export async function callAnalyzeStream<T = unknown>(
  body: Record<string, unknown>,
  aoTexto: (textoAcumulado: string) => void
): Promise<T> {
  const startTime = Date.now();
  const teste = lerModeloTeste();
  if (teste) {
    body = { ...body, _modeloTeste: teste };
    console.log(`🧪 [analyzeClient] modo de teste: ${descreverModoTeste(teste)}`);
  }

  const resp = await fetchAutenticado(
    ENDPOINT_ANALYZE,
    { ...body, stream: true },
    "analyzeClient/stream"
  );
  if (resp === null) {
    throw new Error(
      "[analyzeClient] Usuário não autenticado — refaça login para continuar"
    );
  }

  const tipo = resp.headers.get("content-type") || "";
  if (!tipo.includes("ndjson") || !resp.body) {
    // Servidor não transmitiu (ou algo no caminho juntou tudo): JSON de hoje.
    console.log("🌊 [B389] resposta sem fluxo — lendo como JSON");
    const json = (await resp.json()) as T;
    console.log(`🌐 [analyzeClient] ${ENDPOINT_ANALYZE} OK em ${Date.now() - startTime}ms`);
    return reidentificarObjeto(json);
  }

  const final = await new Promise<T>((resolve, reject) => {
    const leitor = criarLeitorNdjson<T>({
      aoTexto: (acumulado) => aoTexto(reidentificarTexto(acumulado)),
      aoFim: resolve,
      aoErro: (mensagem) => reject(new Error(`[analyzeClient/stream] ${mensagem}`)),
    });
    const reader = resp.body!.getReader();
    const decoder = new TextDecoder("utf-8");
    const ler = (): Promise<void> =>
      reader.read().then(({ done, value }) => {
        if (done) {
          leitor.alimentar(decoder.decode());
          leitor.finalizar();
          return;
        }
        leitor.alimentar(decoder.decode(value, { stream: true }));
        return ler();
      });
    ler().catch((e) => reject(e instanceof Error ? e : new Error(String(e))));
  });

  console.log(`🌐 [analyzeClient] ${ENDPOINT_ANALYZE} (fluxo) OK em ${Date.now() - startTime}ms`);
  return reidentificarObjeto(final);
}
